const DESIGN_TEMPLATES = [
  { id: "default", name: "Klassisch Schwarz", primary: "#000000", accent: "#171717", bg: "#ffffff", text: "#000000" },
  { id: "blush", name: "Zartrosa", primary: "#d4a5a5", accent: "#ffc0cb", bg: "#fff5f7", text: "#5c3a3a" },
  { id: "beige", name: "Beige Eleganz", primary: "#c9b8a8", accent: "#e8d5c4", bg: "#faf8f5", text: "#4a3f35" },
  { id: "rose", name: "Roségold", primary: "#b76e79", accent: "#e5a5b4", bg: "#fff8f9", text: "#5d2e36" },
  { id: "sage", name: "Salbeigrün", primary: "#87a878", accent: "#b8d4a8", bg: "#f5f9f3", text: "#3d4f35" },
  { id: "lavender", name: "Lavendeltraum", primary: "#9b87c7", accent: "#c8b8e6", bg: "#f9f7fc", text: "#4a3c5d" },
  { id: "mint", name: "Frische Minze", primary: "#98d8c8", accent: "#c5e8dd", bg: "#f3faf8", text: "#2d5a4d" },
  { id: "coral", name: "Korallensonnenuntergang", primary: "#e88d7f", accent: "#f7b5a8", bg: "#fff8f6", text: "#5d322a" },
  { id: "gold", name: "Warmes Gold", primary: "#d4a574", accent: "#e8c9a0", bg: "#fffaf3", text: "#5d4a32" },
  { id: "custom", name: "Eigenes Farbschema", primary: "#1D1D1F", accent: "#4F46E5", bg: "#F8FAFC", text: "#1D1D1F" },
];

export function sanitizeHexColor(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#([0-9a-fA-F]{6})$/.test(raw)) return raw.toUpperCase();
  if (/^#([0-9a-fA-F]{3})$/.test(raw)) {
    const short = raw.slice(1).split("").map((char) => `${char}${char}`).join("");
    return `#${short}`.toUpperCase();
  }
  return fallback;
}

function hexToRgb(hex) {
  const normalized = sanitizeHexColor(hex, "#000000").slice(1);
  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

export function alphaColor(hex, opacity) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function mix(colorA, colorB, ratio) {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  return rgbToHex({
    r: a.r + (b.r - a.r) * ratio,
    g: a.g + (b.g - a.g) * ratio,
    b: a.b + (b.b - a.b) * ratio,
  });
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const normalize = (value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b);
}

export function getThemeTextColor(backgroundHex) {
  return getLuminance(backgroundHex) > 0.55 ? "#1D1D1F" : "#FFFFFF";
}

export function getDesignTemplateById(id) {
  return DESIGN_TEMPLATES.find((item) => item.id === id) || DESIGN_TEMPLATES[0];
}

export function resolveDesignTheme(source) {
  const templateId = String(source?.theme_template || "default");
  const baseTemplate = getDesignTemplateById(templateId);
  if (templateId !== "custom") return baseTemplate;

  const primary = sanitizeHexColor(source?.custom_theme_primary, baseTemplate.primary);
  const accent = sanitizeHexColor(source?.custom_theme_accent, baseTemplate.accent);
  const bg = sanitizeHexColor(source?.custom_theme_bg, baseTemplate.bg);
  return {
    ...baseTemplate,
    primary,
    accent,
    bg,
    text: getThemeTextColor(bg),
  };
}

export function getSidebarTheme(source) {
  const theme = resolveDesignTheme(source);
  const base = sanitizeHexColor(theme.primary, "#111111");
  const accent = sanitizeHexColor(theme.accent, base);
  return {
    background: `linear-gradient(180deg, ${mix(base, "#000000", 0.08)} 0%, ${mix(base, "#000000", 0.24)} 100%)`,
    border: mix(base, "#FFFFFF", 0.14),
    panel: mix(base, "#FFFFFF", 0.08),
    panelStrong: mix(base, "#FFFFFF", 0.12),
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.72)",
    subtle: "rgba(255,255,255,0.52)",
    accent,
    accentSoft: alphaColor(accent, 0.18),
    accentBorder: alphaColor(accent, 0.38),
    accentGlow: alphaColor(accent, 0.18),
  };
}

export { DESIGN_TEMPLATES };
